import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useSearchParams } from "react-router-dom";
import { MdSend } from "react-icons/md";
import './Styles.css';

const PetAlertChat = () => {
  const location = useLocation();
  const { tagId, latitude, longitude, responseBody } = location.state || {}; // Extract latitude and longitude from state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const [searchParams] = useSearchParams();
  const chatSessionId = searchParams.get("SessionId");
  const [showPopup, setShowPopup] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [popupError, setPopupError] = useState('');
  const [storeToOpen, setStoreToOpen] = useState(null); // 'google' or 'apple'

  // Fetch mock messages on mount
  useEffect(() => {
    let intervalId = null;

    function startPolling() {
        fetchMessages().then(setMessages);
        intervalId = setInterval(() => {
            fetchMessages().then(setMessages);
        }, 60000); // Poll every 60 seconds
    }

    function stopPolling() {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    }

    function handleVisibilityChange() {
        if (document.visibilityState === 'visible') {
            startPolling(); // start polling when visible
        } else {
            stopPolling(); // stop polling when not visible
        }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start polling if visible on mount
    if (document.visibilityState === 'visible') {
        startPolling();
    }

    // Cleanup on unmount
    return () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Scroll to bottom on new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input) return;
    setMessages([
      ...messages,
      {
        id: Date.now(),
        text: input,
        sender: 'user',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ]);
    setInput('');
  };

  const fetchMessages = async () => {
    const cacheKey = `AlertMessages_${chatSessionId}`;
    let lastId = null;
    let cachedMessages = [];
    try {
        // Load cached messages
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
        cachedMessages = JSON.parse(cached);
        if (cachedMessages.length > 0) {
            lastId = cachedMessages[cachedMessages.length - 1].id;
        }
        }

        // Build URL with afterId param if available
        let url = `https://systalk-dev.azure-api.net/wagtag/api/Alert/AlertChatSession?chatSessionId=${chatSessionId}`;
        if (lastId) {
        url += `&afterId=${lastId}`;
        } else {
            url += `&afterId=0`; // Ensure we start from the beginning if no lastId
        }

        const response = await fetch(url);
        const data = await response.json();

        // Map API data to your message format
        const newMessages = (data.AlertChat || []).map(msg => ({
            id: msg.AlertChatId,
            text: msg.Message,
            sender: msg.Username === 'anon' ? 'user' : 'owner',
            time: formatLocalDate(msg.DateTimeCreated),
            photo: null,
        }));

        // Merge and deduplicate
        const seen = new Set();
        const allMessages = [...cachedMessages, ...newMessages].filter(msg => {
            if (seen.has(msg.id)) return false;
            seen.add(msg.id);
            return true;
        });

        // Update cache and state
        localStorage.setItem(cacheKey, JSON.stringify(allMessages));
        return allMessages;
    } catch (err) {
            console.error("[fetchMessages] Error:", err);
            // On error, fallback to cache
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
            console.log("[fetchMessages] Returning cached messages due to error.");
            return JSON.parse(cached);
        }
        return [];
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    const newMessage = {
      ChatSessionId: chatSessionId,
      Message: input,
    };
    setInput("");
    setLoading(true);
    try {
      const url = "https://systalk-dev.azure-api.net/wagtag/api/Alert/AlertChatSession";
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newMessage),
      });
      // Refetch messages after sending
      const updatedMessages = await fetchMessages();
      setMessages(updatedMessages);
    } catch (err) {
      // Optionally show error or retry
      // e.g. setError("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const handleAppStoreClick = (store) => {
    setStoreToOpen(store);
    setShowPopup(true);
    setPopupError('');
  };

  // Refactored: handleAccountAndRedirect for popup button
  const handleAccountAndRedirect = async () => {
    setPopupError('');
    if (!phoneNumber.match(/^\+?\d{8,15}$/)) {
      setPopupError('Please enter a valid phone number.');
      return;
    }
    try {
      const url = 'https://systalk-dev.azure-api.net/wagtag/api/Account/Register';
      const payload = {
        PhoneNumber: phoneNumber,
        ChatSessionId: chatSessionId,
      };
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      // Detect device and redirect
      if (storeToOpen === 'google') {
        window.location.href = 'https://play.google.com/store/apps/details?id=com.wagtag';
      } else {
        window.location.href = 'https://apps.apple.com/au/app/wagtag/id6738763040';
      }
    } catch (err) {
      setPopupError('Failed to create account. Please try again.');
    }
  };

  function formatLocalDate(dateString) {
        if (!dateString) return "";
        // Always force UTC parsing
        const date = new Date(dateString.endsWith("Z") ? dateString : dateString + "Z");
        // Format: 1:45PM 16 June 2025
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        const day = date.getDate();
        const month = date.toLocaleString(undefined, { month: "long" });
        const year = date.getFullYear();
        return `${hours}:${minutes}${ampm} ${day} ${month} ${year}`;
  }

  return (
    <div>
      {/* Thank You Section */}
      <div style={styles.header}>
        <div style={styles.headerImageContainer}>
          <img
            src="https://wagtagprod.blob.core.windows.net/public/banner.png"
            alt="WagTag"
            style={{ width: "100%", borderRadius: "8px" }}
          />
        </div>
      </div>
      <div style={styles.container}>
        <div style={styles.infoSection}>
          <div style={styles.infoText}>
            {responseBody}
          </div>
          <div style={styles.infoText}>
            Install our app to join the WagTag community and continue chatting with the pet owner, or bookmark this page and continue the chat below.
          </div>
          <div align="center" style={{ paddingTop: "10px" }}>
            <button
              onClick={() => handleAppStoreClick('google')}
              style={{ background: 'none', border: 'none', padding: 0, marginRight: '10px', cursor: 'pointer' }}
            >
              <img src="https://wagtagprod.blob.core.windows.net/public/GooglePlayStore.png" alt="Get it on Google Play" width="150" style={{ verticalAlign: "middle" }} />
            </button>
            <button
              onClick={() => handleAppStoreClick('apple')}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              <img src="https://wagtagprod.blob.core.windows.net/public/AppleAppStore.png" alt="Download on the App Store" width="150" style={{ verticalAlign: "middle" }} />
            </button>
          </div>
        </div>
      </div>
      {/* Popup Modal for phone number */}
      {showPopup && (
        <div style={popupStyles.overlay}>
          <div style={popupStyles.modal}>
            <div style={popupStyles.infoSection}>
              <div style={popupStyles.infoText}>
                <b>Enter your phone number to create your WagTag free account and continue the chat from the WagTag app.</b><br /><br />
                This phone number will not be shared with anyone, and is only used to create your free WagTag account and link your chat with the pet owner.
              </div>
            </div>
            <input
              type="tel"
              placeholder="Phone number (e.g. 0412345678)"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              style={popupStyles.input}
              autoFocus
            />
            {popupError && <div style={popupStyles.error}>{popupError}</div>}
            <div style={{ display: 'flex', flexDirection: 'row', gap: 12, width: '100%', marginTop: 16, justifyContent: 'center' }}>
              <button
                onClick={handleAccountAndRedirect}
                style={{
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '12px 28px',
                  fontSize: 17,
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  flex: 1,
                }}
              >
                Get the App
              </button>
              <button onClick={() => setShowPopup(false)} style={{...popupStyles.closeBtn, marginTop: 0, flex: 1}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Chat Section */}
      <div style={styles.container}>
        <div style={styles.infoSection}>
            <div className="chat-messages">
            {messages.length === 0 ? (
                <div className="chat-empty">
                <p>Start a chat to help reunite this pet with its owner.</p>
                </div>
            ) : (
                messages.map((msg) => (
                <div
                    key={msg.id}
                    className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'owner'}`}
                >
                    {msg.photo && (
                    <img src={msg.photo} alt="Pet" className="chat-photo" />
                    )}
                    {msg.text && <span>{msg.text}</span>}
                    <div className="chat-time">{msg.time}</div>
                </div>
                ))
            )}
            <div ref={chatEndRef} />
            </div>
            <form
            className="chat-input-bar"
            onSubmit={async (e) => {
                e.preventDefault();
                await sendMessage();
            }}
            >
                <input
                    type="text"
                    placeholder="Type a message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="message-input"
                />
                <button
                  type="submit"
                  className="send-btn"
                  disabled={!input}
                  style={{ background: "none", border: "none", padding: 0, paddingTop: 2, marginLeft: 8, cursor: "pointer" }}
                  aria-label="Send"
                >
                  <MdSend size={28} color={(!input) ? "#bbb" : "#4CAF50"} />
                </button>
            </form>
            </div>
      </div>
      {/* Test Open App Button */}
      <div style={{ textAlign: 'center', margin: '20px 0' }}>
        <button
          onClick={() => {
            // Try to open the WagTag app
            const schemeUrl = 'wagtag://alerts';
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = schemeUrl;
            document.body.appendChild(iframe);
            setTimeout(() => {
              document.body.removeChild(iframe);
            }, 1000);
          }}
          style={{
            background: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            padding: '12px 28px',
            fontSize: 17,
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          Test Open WagTag App
        </button>
      </div>
    </div>
  );
};

const styles = {
    container: {
        textAlign: "center",
        paddingLeft: "20px",
        paddingRight: "20px",
    },
    header: {
        backgroundColor: "#4CAF50",
        color: "white",
        padding: "10px 10px",
    },
    headerText: {
        marginLeft: "10px",
        fontSize: "24px",
        fontWeight: "bold",
    },
    headerImageContainer: {
        width: "60%",
        //height: "120px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
    imagePreviewContainer: {
        width: "120px",
        height: "120px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid #ccc",
        borderRadius: "8px",
        backgroundColor: "#f9f9f9",
        flexShrink: 0,
    },
    imagePreview: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "8px",
    },
    title: {
        fontSize: "24px",
        fontWeight: "bold",
        color: "#4CAF50",
    },
    message: {
        fontSize: "18px",
        margin: "20px 0",
    },
    subscriptionContainer: {
        margin: "20px 0",
    },
    input: {
        padding: "10px",
        margin: "10px 0",
        fontSize: "16px",
        width: "50%",
        marginRight: 10,
    },
    button: {
        padding: "10px 20px",
        backgroundColor: "#4CAF50",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
    },
    successMessage: {
        color: "green",
        marginTop: "10px",
    },
    errorMessage: {
        color: "red",
        marginTop: "10px",
    },
    backButton: {
        padding: "10px 20px",
        backgroundColor: "#4CAF50",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        marginTop: "20px",
    },
    infoSection: {
        backgroundColor: "#f9f9f9", // Light gray background
        border: "1px solid #ddd",  // Subtle border
        borderRadius: "8px",       // Rounded corners
        padding: "16px",           // Spacing inside the box
        margin: "16px 0",          // Spacing outside the box
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // Light shadow for depth
    },
    infoText: {
        fontSize: "16px",          // Slightly larger text
        color: "#333",             // Dark gray text for readability
        lineHeight: "1.5",         // Better line spacing
    },
};

const popupStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.4)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    background: '#fff',
    borderRadius: 10,
    padding: 32,
    minWidth: 320,
    maxWidth: 400,
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  infoSection: {
    marginBottom: 16,
    background: '#f9f9f9',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: 12,
    width: '100%',
    textAlign: 'center',
  },
  infoText: {
    fontSize: 15,
    color: '#333',
    lineHeight: 1.5,
  },
  input: {
    width: '100%',
    padding: 10,
    fontSize: 16,
    borderRadius: 6,
    border: '1px solid #ccc',
    marginBottom: 8,
    marginTop: 8,
  },
  error: {
    color: 'red',
    marginBottom: 8,
    fontSize: 14,
  },
  closeBtn: {
    marginTop: 16,
    background: '#eee',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    cursor: 'pointer',
    fontSize: 15,
  },
};

export default PetAlertChat;